import React, { RefObject } from "react";
import { Search, UserRound, Building2, FileWarning, Sparkles, FileSpreadsheet } from "lucide-react";
import NumField from "./NumField";

// Для быстрой проверки без набора ИНН вручную.
const SUGGESTIONS = [
  { name: "ПАО «TOP банк»", inn: "7707083893" },
  { name: "АО «Тандер» (сеть «Магнит»)", inn: "2310031475" },
  { name: "ПАО «ЛУКОЙЛ»", inn: "7708503727" },
];

interface Props {
  inn: string;
  onInnChange: (v: string) => void;
  innType: "ul" | "ip" | "invalid";
  desiredLimit: string;
  onDesiredLimitChange: (v: string) => void;
  isLoading: boolean;
  reportFile: File | null;
  onReportFileChange: (f: File | null) => void;
  reportInputRef: RefObject<HTMLInputElement | null>;
  onSubmit: (e: React.FormEvent) => void;
  onCalculateWithReport: () => void;
  onSuggestionClick: (inn: string) => void;
}

// Карточка «Параметры проверки» для авторизованного пользователя: ИНН, желаемый
// лимит с быстрыми пресетами, запуск расчёта, загрузка своей отчётности, демо-ИНН.
// Чистая презентация — вся логика расчёта (handleCalculate*) остаётся в App.tsx.
export default function AnalysisSearchForm({
  inn, onInnChange, innType, desiredLimit, onDesiredLimitChange, isLoading,
  reportFile, onReportFileChange, reportInputRef, onSubmit, onCalculateWithReport,
  onSuggestionClick,
}: Props) {
  return (
    <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-lg shadow-slate-100/50 space-y-5">
      <div className="space-y-1">
        <h3 className="font-extrabold text-slate-900 tracking-tight font-display flex items-center gap-2">
          <Search className="w-5 h-5 text-indigo-650 shrink-0" />
          Параметры проверки
        </h3>
        <p className="text-xs text-slate-500">Задайте реквизиты и кредитные требования для проведения экспресс-оценки</p>
      </div>

      <form onSubmit={onSubmit} className="space-y-4">
        {/* INN Input + type badge */}
        <div className="space-y-1.5">
          <label htmlFor="inn-input-field" className="block text-xs font-semibold text-slate-700">
            ИНН контрагента <span className="text-indigo-500 font-mono">*</span>
          </label>
          <div className="relative">
            <input
              type="text"
              inputMode="numeric"
              autoComplete="off"
              placeholder="10 цифр — юрлицо, 12 — предприниматель"
              value={inn}
              onChange={(e) => onInnChange(e.target.value.replace(/\D/g, "").slice(0, 12))}
              maxLength={12}
              className="w-full pl-4 pr-11 py-3 bg-slate-50/70 border border-slate-200 hover:border-slate-350 focus:border-indigo-550 focus:bg-white focus:ring-4 focus:ring-indigo-100/40 rounded-xl outline-none transition-all font-mono font-bold text-slate-800 placeholder:text-slate-400 text-base sm:text-sm"
              id="inn-input-field"
              required
            />
            <div className="absolute right-3.5 top-3.5 text-slate-400">
              {innType === "ip" ? <UserRound className="w-4 h-4" /> : <Building2 className="w-4 h-4" />}
            </div>
          </div>
          {innType !== "invalid" && (
            <div className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-100">
              {innType === "ul" ? "🏢 Юридическое лицо" : "👤 Индивидуальный предприниматель"}
            </div>
          )}
        </div>

        {/* Desired Limit Input with quick actions */}
        <div className="space-y-2">
          <div className="flex flex-wrap gap-1 justify-between items-center">
            <label htmlFor="limit-input-field" className="block text-xs font-semibold text-slate-700">
              Желаемый лимит (гарантии/кредиты) <span className="text-slate-400 font-normal">(руб.)</span>
            </label>
            <span className="text-[10px] font-mono text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded font-bold">Q6 параметр</span>
          </div>
          <div className="relative">
            <NumField
              money
              placeholder="Например: 10 000 000"
              value={desiredLimit}
              onChange={(v) => onDesiredLimitChange(v.replace(/^-/, ""))}
              className="w-full pl-4 pr-8 py-3 bg-slate-50/70 border border-slate-200 hover:border-slate-350 focus:border-indigo-550 focus:bg-white focus:ring-4 focus:ring-indigo-100/40 rounded-xl outline-none transition-all font-mono font-bold text-slate-800 placeholder:text-slate-400 text-base sm:text-sm"
              id="limit-input-field"
            />
            <span className="absolute right-3.5 top-3 text-sm font-semibold text-slate-400 font-mono">₽</span>
          </div>

          {/* Quick limit presets */}
          <div className="flex flex-wrap gap-1.5 pt-1">
            {[
              { label: "5 млн", val: "5000000" },
              { label: "10 млн", val: "10000000" },
              { label: "30 млн", val: "30000000" },
              { label: "50 млн", val: "50000000" },
              { label: "100 млн", val: "100000000" }
            ].map((preset) => (
              <button
                key={preset.val}
                type="button"
                onClick={() => onDesiredLimitChange(preset.val)}
                className={`text-xs px-2.5 py-1.5 rounded bg-slate-50 hover:bg-indigo-50 active:bg-indigo-100 text-slate-600 hover:text-indigo-700 border border-slate-200 hover:border-indigo-200 font-semibold font-mono transition-all cursor-pointer ${
                  desiredLimit === preset.val ? "bg-indigo-600 text-white hover:bg-indigo-700 border-indigo-600 hover:border-indigo-700" : ""
                }`}
              >
                {preset.label}
              </button>
            ))}
          </div>
        </div>

        {/* Submit Button */}
        {/* У предпринимателя расчёт не запускается без приложенной отчётности:
            бухгалтерской отчётности он не сдаёт, и считать попросту не из чего. */}
        {innType === "ip" && !reportFile && (
          <div className="flex items-start gap-2 bg-amber-50 border border-amber-100 text-amber-800 text-xs rounded-xl p-3">
            <FileWarning className="w-4 h-4 shrink-0 mt-0.5" />
            <span>
              Для индивидуального предпринимателя приложите годовую отчётность —
              расчёт выполняется только по ней. Файл выбирается ниже.
            </span>
          </div>
        )}
        <button
          type="submit"
          disabled={isLoading || !inn.trim() || innType === "invalid" || (innType === "ip" && !reportFile)}
          className="w-full py-3 px-4 bg-gradient-to-r from-indigo-600 to-indigo-750 hover:from-indigo-700 hover:to-indigo-850 disabled:from-slate-300 disabled:to-slate-400 text-white font-bold text-xs uppercase tracking-wider rounded-xl transition-all shadow-md shadow-indigo-150/50 hover:shadow-lg focus:ring-4 focus:ring-indigo-100 flex items-center justify-center gap-2 cursor-pointer mt-1"
        >
          {isLoading ? (
            <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
          ) : (
            <Sparkles className="w-4 h-4" />
          )}
          Запустить экспресс-анализ
        </button>
      </form>

      {/* Расчёт по загруженной отчётности пользователя */}
      <div className="space-y-2 border-t border-slate-100 pt-4">
        <span className="text-[10px] font-mono font-bold text-slate-400 block tracking-wider uppercase">
          {innType === "ip" ? "Годовая отчётность — обязательна" : "Или рассчитать по вашей отчётности"}
        </span>
        <input ref={reportInputRef} type="file" accept=".csv,.xlsx,.pdf,.png,.jpg,.jpeg,.tiff,.webp" className="hidden"
          onChange={(e) => onReportFileChange(e.target.files?.[0] ?? null)} />
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => reportInputRef.current?.click()}
            className="flex-1 text-left py-2 px-3 bg-slate-50 border border-slate-200 hover:border-slate-300 rounded-xl text-xs text-slate-600 truncate cursor-pointer">
            {reportFile ? `📄 ${reportFile.name}` : "Выбрать файл (Excel/CSV/PDF/скан)…"}
          </button>
          {reportFile && (
            <button type="button" title="Убрать файл"
              onClick={() => { onReportFileChange(null); if (reportInputRef.current) reportInputRef.current.value = ""; }}
              className="text-slate-400 hover:text-rose-600 text-sm cursor-pointer px-1">✕</button>
          )}
        </div>
        <button type="button" onClick={onCalculateWithReport}
          disabled={isLoading || !inn.trim() || !reportFile}
          className="w-full py-2.5 px-4 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 text-white font-bold text-xs uppercase tracking-wider rounded-xl transition-all shadow-md flex items-center justify-center gap-2 cursor-pointer">
          <FileSpreadsheet className="w-4 h-4" />
          {innType === "ip" ? "Распознать отчётность и рассчитать" : "Посчитать по вашей текущей отчётности"}
        </button>
        <p className="text-[10px] text-slate-400 leading-relaxed">
          Базовые методики посчитаются по строкам баланса из файла (коды 1100–2330) вместо данных за последний завершённый год. Поддержка Excel/CSV/PDF и сканов (PDF без текста, фото/изображения) — скан распознаётся через OCR, суммы стоит сверить. Из PDF берётся первая колонка с цифрами (отчётный период).
        </p>
      </div>

      {/* Suggesters for quick clicks in check list */}
      <div className="space-y-2 border-t border-slate-100 pt-4">
        <span className="text-[10px] font-mono font-bold text-slate-400 block tracking-wider uppercase">Опробовать демонстрационные ИНН:</span>
        <div className="flex flex-col gap-2">
          {SUGGESTIONS.map((item) => (
            <button
              key={item.inn}
              onClick={() => onSuggestionClick(item.inn)}
              className="text-left py-2 px-3 bg-slate-50 border border-slate-100 hover:border-slate-300 hover:bg-indigo-50/20 text-slate-700 rounded-xl hover:-translate-y-0.5 transition-all text-xs flex justify-between items-center group cursor-pointer"
            >
              <div className="space-y-0.5 min-w-0 flex-1 pr-1">
                <span className="font-semibold block text-slate-900 truncate">{item.name}</span>
                <span className="font-mono text-[10px] text-slate-400 group-hover:text-indigo-600 font-medium transition-colors">ИНН: {item.inn}</span>
              </div>
              <Sparkles className="w-3.5 h-3.5 text-slate-300 group-hover:text-indigo-600 transition-colors shrink-0" />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
